import React, { useState, useCallback } from "react"
import { gql, useMutation, useQuery } from "@apollo/client"

import { CardHeading, Modal } from "components"

import Avatar from "react-avatar"
import { Tooltip } from "components"

import BookingCreateForm from "./create"
import BookingSingle from "./single"

import cn from "classnames"
import formatMoney from "utils/formatMoney"
import formatDate from "utils/formatDate"

import { toast } from "react-hot-toast"

const CREATE_BOOKING = gql`
  mutation createBooking($input: CreateBookingInput!) {
    createBooking(input: $input) {
      id
      total
      timestamp
      status

      payments {
        id
        amount
        status
      }

      progress {
        id
        type
        amount
        paidAmount
        status
        executionPaymentDate
      }

      user {
        id
        displayName
        email
      }
    }
  }
`
const DELETE_BOOKING = gql`
  mutation DeleteBooking($id: ID!) {
    deleteBooking(id: $id) {
      id
    }
  }
`
const BOOKINGS = gql`
  query Trip($id: ID!) {
    trip(id: $id) {
      bookings {
        id
        total
        timestamp
        status

        payments {
          id
          amount
          status
        }

        progress {
          id
          type
          amount
          paidAmount
          status
          executionPaymentDate
        }

        user {
          id
          displayName
          email
        }
      }
    }
  }
`

const BookingsContainer = ({ tripId, type }) => {
  const [selectedBooking, setSelectedBooking] = useState(null)
  const [isEditing, setIsEditing] = useState(false)

  const openModal = useCallback(async () => {
    setIsEditing(true)
  }, [setIsEditing])

  const closeModal = useCallback(async () => {
    setIsEditing(false)
  }, [setIsEditing])

  const [createBooking] = useMutation(CREATE_BOOKING)
  // const [updateBooking] = useMutation(UPDATE_BOOKING)
  const [deleteBooking] = useMutation(DELETE_BOOKING)
  const { data, loading, error, refetch } = useQuery(BOOKINGS, {
    variables: { id: tripId },
  })

  const handleCreate = async (input) => {
    try {
      await toast.promise(
        createBooking({
          variables: { input },
          // update(cache, { data }) {
          //   // const existing = cache.readQuery({
          //   //   query: BOOKINGS,
          //   //   variables: { id: tripId },
          //   // })

          //   const existing = data
          //   const existingBookings = existing?.trip?.bookings

          //   cache.writeQuery({
          //     query: BOOKINGS,
          //     variables: { id: tripId },
          //     data: {
          //       trip: {
          //         ...existing?.trip,
          //         bookings: [...existingBookings, data?.createBooking],
          //       },
          //     },
          //   })
          // },
        }),
        {
          loading: "Creating...",
          success: <b>Booking created!</b>,
          error: <b>Could not save.</b>,
        },
      )

      await refetch()
    } catch (error) {
      console.error(error)
    }
  }

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this booking?")) {
      try {
        await toast.promise(
          deleteBooking({
            variables: { id },
            update(cache, { data }) {
              const existing = cache.readQuery({
                query: BOOKINGS,
                variables: { id: tripId },
              })

              const existingBookings = existing?.trip?.bookings
              const deleted = data?.deleteBooking

              cache.writeQuery({
                query: BOOKINGS,
                variables: { id: tripId },
                data: {
                  trip: {
                    ...existing?.trip,
                    bookings: existingBookings.filter(
                      (_) => _.id !== deleted.id,
                    ),
                  },
                },
              })
            },
          }),
          {
            loading: "Deleting...",
            success: <b>Booking deleted!</b>,
            error: <b>Could not save.</b>,
          },
        )

        setSelectedBooking(null)
      } catch (error) {}
    }
  }

  const isEmpty = !data || data?.trip?.bookings?.length === 0
  const title = type === "INDIVIDUAL" ? "Clients" : "Bookings"
  const ctaTitle = `Create ${title.replace("s", "")}`

  return (
    <div className="bg-white rounded-lg shadow-lg">
      {isEmpty ? (
        <>
          <CardHeading title={title} />
          <BookingsEmpty
            title={title}
            ctaTitle={ctaTitle}
            isLoading={loading}
            onClick={openModal}
          />
        </>
      ) : selectedBooking ? (
        <>
          <BookingSingle
            {...selectedBooking}
            onBack={() => setSelectedBooking(null)}
            onDelete={handleDelete}
          />
        </>
      ) : (
        <>
          <CardHeading title={title} ctaTitle={ctaTitle} onClick={openModal} />
          <BookingsTable
            data={data?.trip?.bookings}
            onSelect={setSelectedBooking}
          />
        </>
      )}

      <Modal title={ctaTitle} isOpen={isEditing} onClose={closeModal}>
        <div className="max-w-screen-lg mx-auto ">
          <BookingCreateForm
            tripId={tripId}
            type={type}
            onClose={closeModal}
            onSubmit={handleCreate}
          />
        </div>
      </Modal>
    </div>
  )
}

export default BookingsContainer

const BookingsEmpty = ({ title, ctaTitle, isLoading, onClick }) => {
  return (
    <div className="py-16 flex flex-col justify-center items-center">
      {isLoading ? (
        <div>...Loading...</div>
      ) : (
        <>
          {" "}
          <p className="text-center">No {title}</p>
          <button
            onClick={onClick}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="-ml-1 mr-2 h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              />
            </svg>
            {/* Add booking */}
            {ctaTitle}
          </button>
        </>
      )}
    </div>
  )
}

const BookingsTable = ({ data, onSelect }) => {
  const header = ["Client", "Date", "Status", "Progress"]

  const getPaid = (payments) =>
    payments
      .filter((v) => v.status === "PAID")
      .reduce((acc, val) => acc + val.amount, 0)

  const roundPercentage = (data) => {
    const arr = data.map((v) => v.amount)
    const total = arr.reduce((a, b) => a + b, 0)
    let percentage = arr.map((v) => Math.floor((Math.abs(v) / total) * 100))
    const totalPercentage = percentage.reduce((a, b) => a + b, 0)
    // const minPercetange = Math.min(...percentage)

    let diffrence = 0
    percentage = percentage.map((percent, i) =>
      percent < 10 ? percent + (20 - percent) : percent,
    )

    if (totalPercentage !== 100) {
      const lowest = percentage.indexOf(Math.min(...percentage))
      const percToAdd = 100 - totalPercentage
      percentage = percentage.map((v, i) => (i === lowest ? v + percToAdd : v))
    }

    // const paidPerc =
    // const paidWidth = Math.floor(paidPerc)

    // booking.progress
    return data.map((v, i) => ({
      ...v,
      // width: `${Math.floor((Math.abs(v.amount) / total) * 100)}%`,
      width: `${percentage[i]}%`,
      // width: Math.floor(
      //   Math.abs(Math.floor(Math.abs(v.paidAmount / v.amount) * 100)),
      // ),
    }))
  }

  const bars = (data) => roundPercentage(data)

  return (
    <div style={{ maxHeight: "70vh", overflowY: "scroll" }}>
      <table className="min-w-full table-auto">
        <thead>
          <tr>
            {header.map((item, index) => (
              <th
                key={index}
                className="px-6 py-3 border-b border-gray-200 bg-gray-100 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                style={{ position: "sticky", top: "0" }}
              >
                {item}
              </th>
            ))}
          </tr>
        </thead>

        <tbody className="bg-white">
          {data?.map((booking, index) => (
            <tr
              key={index}
              className="cursor-pointer hover:bg-blue-100"
              onClick={() => onSelect(booking)}
            >
              <td className="w-3/12 px-6 py-4 whitespace-no-wrap border-b border-gray-200">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-10 w-10">
                    <Avatar
                      name={booking?.user?.displayName}
                      // src={user.photoURL}
                      size="40"
                      round={true}
                    />
                  </div>
                  <div className="ml-4">
                    <div className="text-sm leading-5 font-medium text-gray-900">
                      {booking?.user?.displayName}
                    </div>
                    <div className="text-sm leading-5 text-gray-500">
                      {booking?.user?.email}
                    </div>
                  </div>
                </div>
              </td>
              <td className="w-2/12 px-6 py-4 whitespace-no-wrap border-b border-gray-200">
                <div className="text-sm leading-5 text-gray-500">
                  {booking?.timestamp &&
                    formatDate(booking?.timestamp, "dd. MMMM yyyy")}
                </div>
              </td>
              <td className="w-2/12 px-6 py-4 whitespace-no-wrap border-b border-gray-200 text-sm leading-5 text-gray-500">
                <span
                  className={cn(
                    `px-2 inline-flex text-xs leading-5 font-semibold rounded-full`,
                    {
                      [`bg-green-100 text-green-800`]:
                        booking.status === "COMPLETED" ||
                        booking.status === "FINISHED",
                      [`bg-yellow-100 text-yellow-800`]:
                        booking.status === "CREATED",
                      [`bg-red-100 text-red-800`]:
                        booking.status === "CANCELED" || "CANCELED_BY_SYSTEM",
                      [`bg-blue-100 text-blue-800`]:
                        booking.status === "IN_PROGRESS",
                    },
                  )}
                >
                  {booking.status}
                </span>
              </td>
              <td className="flex-1 px-6 py-4 whitespace-no-wrap border-b border-gray-200 text-sm leading-5 font-medium">
                <div className="text-sm leading-5 text-gray-900">
                  {formatMoney(getPaid(booking.payments))} /{" "}
                  {formatMoney(booking.total)}
                </div>

                <div className="bg-gray-100 w-full h-4 flex">
                  {bars(booking.progress)?.map((part, index) => (
                    <>
                      <div
                        data-multiline
                        data-for="tooltip"
                        data-tip={JSON.stringify(part)}
                        key={index}
                        className={cn(`border-r-2`, {
                          [`bg-green-500 `]: part.status === "PAID",
                          [`bg-yellow-500 `]:
                            part.status === "NOT_PAID" || "PARTIAL_PAID",
                          [`bg-red-500 `]:
                            part.status === "LATE" || "CANCELED_BY_SYSTEM",
                          [`bg-blue-500 `]: part.status === "PENDING",
                        })}
                        style={{ width: part.width }}
                      >
                        {part.status === "PARTIAL_PAID" && (
                          <div
                            className="bg-green-500 h-full"
                            style={{
                              width: `${Math.floor(
                                (Math.abs(part.paidAmount) / part.amount) * 100,
                              )}%`,
                            }}
                          />
                        )}
                      </div>
                    </>
                  ))}

                  <Tooltip
                    id="tooltip"
                    place="top"
                    type="dark"
                    effect="solid"
                    getContent={(_part) => {
                      if (!_part) return null
                      const part = JSON.parse(_part)
                      return (
                        <div>
                          {/* <p>{part.id}</p> */}
                          <p>Type: {part?.type}</p>
                          <p>Status: {part?.status?.replace("_", " ")}</p>
                          {part?.executionPaymentDate && (
                            <p>
                              Date:{" "}
                              {formatDate(
                                part?.executionPaymentDate,
                                "dd. MMM yyyy HH:mm",
                              )}
                            </p>
                          )}
                          <p>Amount: {formatMoney(part.amount)}</p>
                        </div>
                      )
                    }}
                  />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
